// tslint:disable:max-file-line-count
import { getKeyboardEventCode, IStyleConfig, mergeStyles, OutsideClick, Popup, TextField } from '@cian/components';
import { scrollTo } from '@cian/components/dist/utils';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import * as textSearch from '../../../utils/text_search';

const style = require('./index.css');

export interface IAutocompleteSuggestion<T> {
  label: string;
  value?: T;
}

export interface IAutocompleteProps<T> {
  suggestions: IAutocompleteSuggestion<T>[];
  placeholder?: string;
  popupStyle?: IStyleConfig;
  popupListStyle?: IStyleConfig;
  popupListItemStyle?: IStyleConfig;

  onSelect(suggestion: IAutocompleteSuggestion<T>): void;
}

export interface IAutocompleteState<T> {
  suggestions: IAutocompleteSuggestion<T>[];
  value: string;
  isOpened: boolean;
  selectedItemIndex: number;
}

export class Autocomplete<T> extends React.Component<IAutocompleteProps<T>, IAutocompleteState<T>> {
  public state = {
    suggestions: this.props.suggestions,
    value: '',
    isOpened: false,
    selectedItemIndex: 0,
  };

  private textField: TextField | null = null;
  private popupElement: HTMLDivElement;
  private suggestionElements: HTMLLIElement[] | null[] = [];
  private mouseOverDisabled = false;
  private mouseOverDisableTimeout: number;

  public render() {
    return (
      <OutsideClick
        onOutsideClick={this.handleBlur}
        rootElement={<div {...mergeStyles(style['container'], this.state.isOpened && style['container--open'])} />}
      >
        <TextField
          ref={textField => (this.textField = textField)}
          inputStyle={style.input}
          placeholder={this.props.placeholder}
          value={this.state.value}
          onValueChange={this.handleValueChange}
          onFocus={this.handleFocus}
          onKeyDown={this.handleKeyDown}
        />
        <button className={style.button} onClick={() => this.toggle()}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 6">
            {/* tslint:disable-next-line:max-line-length */}
            <path
              fill="currentColor"
              fillRule="evenodd"
              d="M8.372.28L5 3.65 1.628.28A.95.95 0 0 0 .28.28a.952.952 0 0 0 0 1.348l4.046 4.046a.954.954 0 0 0 1.35 0L9.72 1.628A.954.954 0 0 0 8.372.278z"
            />
          </svg>
        </button>
        {this.renderPopup()}
      </OutsideClick>
    );
  }

  private renderPopup() {
    if (!this.state.isOpened) {
      return null;
    }

    return (
      <Popup
        ref={popup => (this.popupElement = ReactDOM.findDOMNode(popup as Popup) as HTMLDivElement)}
        containerStyle={
          mergeStyles(
            style.popup,
            this.props.popupStyle,
            // tslint:disable-next-line:no-any
          ) as any
        }
      >
        {this.renderList()}
      </Popup>
    );
  }

  private renderList() {
    if (this.state.suggestions.length === 0) {
      return <div className={style['nothingFound']}>Ничего не найдено</div>;
    }

    this.suggestionElements = [];

    return (
      <ul {...mergeStyles(style.list, this.props.popupListStyle)}>
        {this.state.suggestions.map((suggestion, index) => {
          return (
            <li
              key={`suggestion-${suggestion.label}`}
              ref={item => (this.suggestionElements[index] = item)}
              {...mergeStyles(
                style['list-item'],
                this.state.selectedItemIndex === index && style['list-item--selected'],
                this.props.popupListItemStyle,
              )}
            >
              <a onClick={() => this.handleItemClick(suggestion)} onMouseOver={() => this.handleItemOver(index)}>
                {this.renderLabel(suggestion.label)}
              </a>
            </li>
          );
        })}
      </ul>
    );
  }

  private renderLabel(label: string) {
    const value = this.state.value.trim();

    if (!value) {
      return <span>{label}</span>;
    }

    let parts: JSX.Element[] = [];
    const highlights = textSearch.match(label, value).matches;
    if (highlights.length === 0) {
      return <span>{label}</span>;
    }

    highlights.forEach((match, index) => {
      const lastIndex = (highlights[index - 1] && highlights[index - 1][1]) || 0;
      if (match[0] !== lastIndex) {
        parts.push(<span>{label.substring(lastIndex, match[0])}</span>);
      }
      parts.push(<strong>{label.substring(match[0], match[1])}</strong>);
    });
    const lastIndex = highlights[highlights.length - 1][1];
    if (label.length - 1 > lastIndex - 1) {
      parts.push(<span>{label.substring(lastIndex)}</span>);
    }

    return <span>{parts}</span>;
  }

  private handleFocus = () => {
    this.open();
  };

  private handleBlur = () => {
    this.close();
  };

  private handleValueChange = (value: string) => {
    this.setState({
      value,
      suggestions: filterSuggestions(this.props.suggestions, value.trim()),
      selectedItemIndex: 0,
    });
  };

  private handleKeyDown = (event: React.KeyboardEvent<EventTarget>) => {
    if (this.state.suggestions.length === 0) {
      return;
    }

    const nativeEvent = event.nativeEvent as KeyboardEvent;
    const code = getKeyboardEventCode(nativeEvent);

    switch (code) {
      case 'ArrowDown':
      case 'Down':
        event.preventDefault();
        this.selectItem(this.state.selectedItemIndex + 1);
        break;

      case 'ArrowUp':
      case 'Up':
        event.preventDefault();
        this.selectItem(this.state.selectedItemIndex - 1);
        break;

      case 'Tab':
      case 'NumpadEnter':
      case 'Enter':
        event.preventDefault();
        this.handleItemClick(this.state.suggestions[this.state.selectedItemIndex]);
        break;
      case 'Escape':
        this.close();
        break;
      default:
        break;
    }
  };

  private handleItemOver = (index: number) => {
    if (this.mouseOverDisabled) {
      return;
    }

    this.setState({
      selectedItemIndex: index,
    });
  };

  private handleItemClick = (item: IAutocompleteSuggestion<T>) => {
    this.props.onSelect(item);
    this.close();
  };

  private selectItem = (index: number) => {
    if (index < 0) {
      // tslint:disable-next-line:no-parameter-reassignment
      index = this.state.suggestions.length - 1;
    } else if (index > this.state.suggestions.length - 1) {
      // tslint:disable-next-line:no-parameter-reassignment
      index = 0;
    }

    if (this.state.selectedItemIndex === index) {
      return;
    }

    this.setState({
      selectedItemIndex: index,
    });

    this.scrollTo(index);
  };

  private open() {
    if (this.textField) {
      this.textField.focus();
      this.setState({ isOpened: true });
    }
  }

  private close() {
    if (this.textField) {
      this.textField.blur();

      this.setState({
        value: '',
        suggestions: this.props.suggestions,
        isOpened: false,
        selectedItemIndex: 0,
      });
    }
  }

  private toggle() {
    if (!this.state.isOpened) {
      this.open();
    } else {
      this.close();
    }
  }

  private scrollTo(index: number, center: boolean = false) {
    const containerElement = this.popupElement;
    const valueElement = this.suggestionElements[index];

    if (!valueElement) {
      return;
    }

    window.clearTimeout(this.mouseOverDisableTimeout);
    this.mouseOverDisabled = true;

    scrollTo({ center, containerElement, targetElement: valueElement });

    this.mouseOverDisableTimeout = window.setTimeout(() => {
      this.mouseOverDisabled = false;
    }, 100);
  }
}

function filterSuggestions<T>(suggestions: IAutocompleteSuggestion<T>[], text: string) {
  if (!text) {
    return suggestions;
  }

  return suggestions
    .map(suggestion => ({
      suggestion,
      occurrences: textSearch.match(suggestion.label, text),
    }))
    .filter(item => item.occurrences.accuracy > 0)
    .sort((a, b) => b.occurrences.accuracy - a.occurrences.accuracy)
    .map(item => item.suggestion);
}
